// web/src/tutorial/detail/key-ripple.ts
// Key Ripple · 软件界面详细说明（逐页签、逐参数）
// 依据：H:\key_ripple_rust
//   src/ui/app.rs（页签结构）、avatar_panel.rs / avatar_display.rs / avatar_manager.rs、
//   midi_panel.rs、execution_panel.rs、hand_recorder_reader.rs、componenets.rs、
//   data.rs（默认值）、asset/i18n/zh-CN.json 与 en-US.json（界面原文，参数名以此为准）
//
// 写作原则（作者要求）：按截图上的分区从上到下逐块讲，每个可调参数都要讲到
// 「管什么 / 什么时候调 / 调了会怎样」；只讲界面上能看到的东西。

import { l } from "../l";
import type { TutorialDoc } from "../types";

export const keyRippleDetail: TutorialDoc = {
  key: "key_ripple",
  slug: "key-ripple",
  kind: "detail",
  icon: "🎹",
  title: l("Key Ripple 软件界面详细说明", "Key Ripple UI Reference"),
  subtitle: l(
    "按界面的四个页签逐区展开：每个按钮点下去会发生什么、每个参数控制什么、范围和默认值是多少、什么时候值得调整。",
    "A screen-by-screen walkthrough of all four tabs: what every button does, and what each parameter controls, its range, its default, and when you would want to change it.",
  ),
  readMinutes: 14,
  video: {
    hidden: true,
    caption: l("本页不内嵌视频", "No embedded video on this page"),
  },
  images: [
    {
      src: "/assets/image/products/key-ripple/CN/角色设置.png",
      srcEn: "/assets/image/products/key-ripple/EN/Avatar.png",
      label: l("角色设置", "Avatar"),
      width: 1002,
      height: 955,
    },
    {
      src: "/assets/image/products/key-ripple/CN/MIDI处理.png",
      srcEn: "/assets/image/products/key-ripple/EN/MIDI.png",
      label: l("MIDI 处理", "MIDI"),
      width: 1002,
      height: 955,
    },
    {
      src: "/assets/image/products/key-ripple/CN/执行操作.png",
      srcEn: "/assets/image/products/key-ripple/EN/Execute.png",
      label: l("执行操作", "Execute"),
      width: 1002,
      height: 955,
    },
    {
      src: "/assets/image/products/key-ripple/CN/记录查看.png",
      srcEn: "/assets/image/products/key-ripple/EN/Hand Recorder Viewer.png",
      label: l("记录查看", "Records"),
      width: 1920,
      height: 1040,
    },
  ],
  sections: [
    // ─────────────────────────────────────────────────────────────
    {
      id: "how-to-read",
      title: l("这篇怎么用", "How to use this page"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "这一页是**参数查询手册**：按软件界面的页签顺序，把每一屏从上到下拆开，逐个说明界面上的按钮和可调参数——它管什么、什么时候需要动、往大调还是往小调。想先跑通一次完整流程（选演奏者 → 选 MIDI → 生成动画），看[快速上手](/quickstart/key-ripple.html)。",
            "This page is a **reference**: it walks the app tab by tab, top to bottom, and explains every button and adjustable parameter — what it controls, when to touch it, and which way to move it. To get one full run working first (pick a performer → pick a MIDI → generate), read the [Quick Start](/quickstart/key-ripple.html).",
          ),
        },
        {
          kind: "table",
          head: [
            l("页签", "Tab"),
            l("本文哪一节", "Section below"),
            l("悬浮窗截图", "Screenshot"),
          ],
          rows: [
            [
              l("⚙ 角色设置", "⚙ 角色设置 / Avatar"),
              l(
                "选演奏者、看角色信息、调动作参数。",
                "Pick the performer, inspect its info, tune the motion parameters.",
              ),
              l("截图 1", "Shot 1"),
            ],
            [
              l("🎵 MIDI处理", "🎵 MIDI处理 / MIDI"),
              l(
                "选 MIDI 文件、扫描轨道、设置通道 / FPS / 量化。",
                "Load the MIDI, scan tracks, set channel / quantization.",
              ),
              l("截图 2", "Shot 2"),
            ],
            [
              l("▶ 执行操作", "▶ 执行操作 / Execute"),
              l(
                "核对设置、生成指法与动画、看控制台。",
                "Review the settings, generate fingering and animation, read the console.",
              ),
              l("截图 3", "Shot 3"),
            ],
            [
              l("📋 记录查看", "📋 记录查看 / Records"),
              l(
                "逐帧检查生成出来的手部记录。",
                "Inspect the generated hand records frame by frame.",
              ),
              l("截图 4", "Shot 4"),
            ],
          ],
        },
        {
          kind: "note",
          text: l(
            "右下角悬浮窗里就是这些界面截图，中英文各一份，跟着站点语言切换；也可以点窗口上的页签自己翻。正文滚到哪一节，窗口会自动切到那一节对应的一张。",
            "The floating viewer in the bottom-right corner shows these screenshots — one set per language, following the site language — and you can switch them yourself with its tabs. Scrolling through the article switches it to the screenshot the current section talks about.",
          ),
        },
      ],
    },

    // ═════════════════════════════════════════════════════════════
    // 页签 1：角色设置
    // ═════════════════════════════════════════════════════════════
    {
      id: "tab-avatar",
      title: l("角色设置：决定「谁在弹」", "Avatar tab: who is playing"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "这一屏回答两个问题：**这次由哪个演奏者弹**，以及**它弹起来是什么手感**。上半部分左右两张卡片负责「选人」，下半整行是七个动作参数。",
            "This screen answers two questions: **which performer plays this time**, and **how the performance feels**. The two cards across the top handle the first; the full-width block underneath holds seven motion parameters.",
          ),
        },
        {
          kind: "p",
          text: l(
            "卡片左上角的小圆标是分区号（01、02……），和截图上的标号一一对应。这里要注意：角色信息 / Avatar Info 和 动作参数 / Motion Parameters 两张卡片都标着 02——那是界面自己的编号，按卡片标题认就行。",
            "The small round badge at each card's top-left is a section number (01, 02, …) matching the screenshots. One quirk: both 角色信息 / Avatar Info and 动作参数 / Motion Parameters are numbered 02; go by the card titles rather than the badges.",
          ),
        },
      ],
      subs: [
        {
          id: "avatar-selection",
          title: l(
            "01 角色选择：换演奏者",
            "01 Avatar Selection: switch performers",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "整页只有这一个下拉菜单能换演奏者。点开它，列表里是软件启动时读到的全部已登记角色；选中的那一个会立刻生效，同时下面的信息框会换成它的数据。",
                "This dropdown is the only place on the page where you switch performers. Open it and you get every performer registered at startup; the one you click takes effect immediately and the info box below switches to its data.",
              ),
            },
            {
              kind: "table",
              head: [
                l("信息", "Field"),
                l("显示什么", "What it shows"),
                l("从哪里来", "Where it comes from"),
              ],
              rows: [
                [
                  l("钢琴键域", "钢琴键域 / Key range"),
                  l(
                    "这个演奏者能弹到的最低键 - 最高键，例如 21 - 108。",
                    "The lowest and highest keys this performer can reach, e.g. 21 - 108.",
                  ),
                  l("角色自己的配置。", "The performer's own profile."),
                ],
                [
                  l("默认手位", "默认手位 / Default hand position"),
                  l(
                    "左右手在键盘上的起始落点，例如 52 / 76。",
                    "Where the left and right hands start on the keyboard, e.g. 52 / 76.",
                  ),
                  l("角色自己的配置。", "The performer's own profile."),
                ],
                [
                  l("单手跨度", "单手跨度 / Hand span"),
                  l(
                    "一只手张开能跨几个键，例如 10 个键。",
                    "How many keys a spread hand covers, e.g. 10 个键 / 10 keys.",
                  ),
                  l(
                    "软件按角色的键盘尺寸与手掌大小自动算出来的，不是手填的。",
                    "Computed automatically from the performer's keyboard size and palm size — not typed in by hand.",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "这三个数都是**只读**的，这个页面上没有地方能改它们。手位偏高、偏低，或者手跨得不够宽，都要回到角色数据本身去解决。想把自建人物做成可用演奏者，见《自定义人物进阶教程》。",
                "All three are **read-only** — nothing on this page edits them. If the hand position sits too high or too low, or the span is too narrow, that has to be fixed in the performer data itself. To turn your own character into a usable performer, see the Custom Performer tutorial.",
              ),
            },
            {
              kind: "note",
              text: l(
                "角色列表为空时，这张卡片显示 暂无角色 和一个 ➕ 新增角色 / Add Avatar 按钮，点它等同于去右边点 ➕ 新增 / Add。",
                "When the list is empty the card shows 暂无角色 / No avatars yet plus a ➕ 新增角色 / Add Avatar button, which does the same thing as ➕ 新增 / Add on the right.",
              ),
            },
          ],
        },
        {
          id: "avatar-info",
          title: l(
            "02 角色信息：角色图与登记表",
            "02 Avatar Info: portrait and registry",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "右上角 角色信息 / Avatar Info 卡片显示当前演奏者的图片，下面三个按钮管的是「角色列表」本身，而不是动作：✏ 修改 / Edit 打开预填好的编辑表单，➕ 新增 / Add 打开一张空表单，🗑 删除 / Delete 弹出删除确认。",
                "The 角色信息 / Avatar Info card in the top-right shows the current performer's picture. Its three buttons manage the performer list itself rather than the motion: ✏ 修改 / Edit opens the edit form pre-filled, ➕ 新增 / Add opens a blank one, and 🗑 删除 / Delete asks for confirmation first.",
              ),
            },
            {
              kind: "table",
              head: [
                l("字段", "Field"),
                l("填什么", "What goes in it"),
                l("注意", "Notes"),
              ],
              rows: [
                [
                  l("名字:", "名字: / Name:"),
                  l(
                    "这个角色在 角色选择 / Avatar Selection 下拉菜单里显示的名字。",
                    "The name this performer shows in the 角色选择 / Avatar Selection dropdown.",
                  ),
                  l(
                    "必填，且不能和已有角色重名。",
                    "Required, and it must not duplicate an existing name.",
                  ),
                ],
                [
                  l("图片:", "图片: / Image:"),
                  l(
                    "卡片里显示的那张角色图，用 📁 浏览 选一张 png / jpg / jpeg / gif。",
                    "The portrait shown in the card; use 📁 浏览 / Browse to pick a png, jpg, jpeg or gif.",
                  ),
                  l(
                    "可以留空，留空时卡片显示 🎹 无图片。",
                    "Optional — an empty field shows 🎹 无图片 / No image.",
                  ),
                ],
                [
                  l("配置:", "配置: / Config:"),
                  l(
                    "角色的 .avatar 文件，用 📁 浏览 / Browse 选一个。",
                    "The performer's .avatar file, picked with 📁 浏览 / Browse.",
                  ),
                  l(
                    "必填。键域、手位、单手跨度都是从它里面读出来的。",
                    "Required. Key range, hand positions and hand span are all read from it.",
                  ),
                ],
              ],
            },
            {
              kind: "ul",
              items: [
                l(
                  "表单底部是 ❌ 取消 和 💾 保存；保存成功后表单关闭、列表刷新，新角色立刻出现在下拉菜单里。",
                  "The form ends with ❌ 取消 / Cancel and 💾 保存 / Save; saving closes the form, refreshes the list and the new performer appears in the dropdown right away.",
                ),
                l(
                  "保存时软件会把图片和 .avatar 复制到自己的资源目录里，所以选完之后原始文件可以挪走。",
                  "Saving copies the image and the .avatar into the app's own asset folders, so the originals can be moved afterwards.",
                ),
                l(
                  "🗑 删除 / Delete 会先弹一个 确认删除 / Confirm Delete 对话框，写清要删的是哪个角色，并提示此操作不可撤销；确认按 ✅ 确认删除，反悔按 ❌ 取消。最后一个角色删不掉。",
                  "🗑 删除 / Delete first opens a 确认删除 / Confirm Delete dialog naming the performer and warning that it cannot be undone; confirm with ✅ 确认删除 or back out with ❌ 取消. The last remaining performer cannot be deleted.",
                ),
              ],
            },
            {
              kind: "callout",
              tone: "info",
              title: l("这一页不做的事", "What this page does not do"),
              text: l(
                "它只负责「登记与选择」——把一个已经存在的 .avatar 配置挂到界面上。它不建模，也不能在这里改键域和手位数字。",
                "This tab only registers and selects — it attaches an existing .avatar profile to the UI. It is not a rigging tool, and the key range and hand-position numbers cannot be edited here.",
              ),
            },
          ],
        },
        {
          id: "avatar-motion-params",
          title: l(
            "02 动作参数：七个可调项",
            "02 Motion Parameters: the seven knobs",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "页面下半整行是 动作参数 / Motion Parameters，左列三个、右列四个，一共七项。这一屏决定了演奏动作的手感——动作僵硬、发飘、赶拍子，基本都是在这里调。数字框可以直接左右拖动，也可以双击输入。",
                "The full-width 动作参数 / Motion Parameters block at the bottom holds seven items: three in the left column, four in the right. This is where the feel of the performance is decided — stiffness, floatiness and rushing all get fixed here. Drag a number box sideways or double-click to type a value.",
              ),
            },
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("作用", "What it controls"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("什么时候调", "When to change it"),
              ],
              rows: [
                [
                  l("抬掌幅度", "抬掌幅度 / Hand lift amplitude"),
                  l(
                    "手掌抬起动作的整体倍率。",
                    "Overall multiplier for the palm-lift motion.",
                  ),
                  l("0.0 – 2.0 x", "0.0 – 2.0 x"),
                  l("1.0 x", "1.0 x"),
                  l(
                    "抬手太夸张就往下调，手像粘在琴键上就往上调；界面提示建议在 0.8x–1.3x 之间微调。",
                    "Lower it if the lift looks exaggerated, raise it if the hands look glued to the keys; the UI suggests fine-tuning between 0.8x and 1.3x.",
                  ),
                ],
                [
                  l("按键耗时", "按键耗时 / Key press duration"),
                  l(
                    "一根手指从上面按到键底花的时间。",
                    "How long a finger takes to travel down and press a key.",
                  ),
                  l("0.01 – 2.0 秒", "0.01 – 2.0 s"),
                  l("0.0875 秒", "0.0875 s"),
                  l(
                    "界面按三位小数显示，所以框里看到的是 0.087 s。快曲子嫌手指落下太慢可以调小，抒情曲可以略大。",
                    "The box shows three decimals, so you see 0.087 s. Lower it if fingers land too slowly on fast pieces; raise it slightly for lyrical ones.",
                  ),
                ],
                [
                  l(
                    "手掌八度移动耗时",
                    "手掌八度移动耗时 / Octave move duration",
                  ),
                  l(
                    "手掌横向移动一个八度（12 个半音）需要的时间。它同时就是「手速上限」。",
                    "Time for the palm to travel one octave (12 semitones) sideways. This doubles as the hand-speed ceiling.",
                  ),
                  l("0.1 – 5.0 秒", "0.1 – 5.0 s"),
                  l("0.2 秒", "0.2 s"),
                  l(
                    "调大 = 手移动更慢更稳、更「像人」；调小 = 更多记录会被判定为来不及而被剔除。",
                    "Larger = slower, steadier, more human hand travel; smaller = more records get culled for being physically unreachable.",
                  ),
                ],
                [
                  l("最大过渡时长", "最大过渡时长 / Max transition duration"),
                  l(
                    "两个音符间隔较远时，允许用来生成过渡动作的最长时间。",
                    "The longest transition motion allowed when the gap between two notes is wide.",
                  ),
                  l("0.5 – 10.0 秒", "0.5 – 10.0 s"),
                  l("3.0 秒", "3.0 s"),
                  l(
                    "长休止、间奏处手僵在原地就调大；动作拖泥带水就调小。",
                    "Raise it if the hands sit frozen during long rests; lower it if transitions drag.",
                  ),
                ],
                [
                  l("抬指时间", "抬指时间 / Lift time"),
                  l(
                    "手指从键上抬起来花的时间。",
                    "How long a finger takes to lift off a key.",
                  ),
                  l("0.01 – 2.0 秒", "0.01 – 2.0 s"),
                  l("0.12 秒", "0.12 s"),
                  l(
                    "默认比 按键耗时 / Key press duration 略慢，看起来最自然，一般不用动；离开键太黏就调小。",
                    "By default it is a little slower than 按键耗时 / Key press duration, which reads naturally; usually leave it alone, or lower it if fingers seem to stick to the keys.",
                  ),
                ],
                [
                  l("转向敏感度", "转向敏感度 / Steering sensitivity"),
                  l(
                    "左右手中点的变化有多容易触发角色朝向切换。这是一个滑块，不是数字框。",
                    "How easily mid-point changes of the two hands trigger a body re-orientation. This one is a slider, not a number box.",
                  ),
                  l("0.0 – 1.0", "0.0 – 1.0"),
                  l("0.750", "0.750"),
                  l(
                    "身体来回晃就调低，一直盯着一侧、不跟着音区走就调高。卡片底部写着两端的含义：0 时要中点变化超过 12 个半音才转，1 时超过 1 个半音就转。",
                    "Lower it if the body sways back and forth, raise it if it stares at one side instead of following the register. The note at the bottom of the card spells out both ends: at 0 the mid-point must shift more than 12 semitones, at 1 more than 1 semitone is enough.",
                  ),
                ],
                [
                  l("速度限制剔除", "速度限制剔除 / Speed limit culling"),
                  l(
                    "勾上以后，指法生成完毕会按手掌移动速度上限，把「物理上按不到」的记录从输出里剔除。它不参与指法的取舍，所以不会改变左右手的分配。",
                    "When ticked, records that physically cannot be reached within the hand-movement speed limit are removed from the output after fingering is generated. It never takes part in fingering decisions, so hand assignment is unaffected.",
                  ),
                  l("启用 / 不勾选", "启用 / off"),
                  l("勾选 启用", "启用 / Enable ticked"),
                  l(
                    "保持勾选。取消勾选后不再剔除任何记录，动作会完整但可能出现人做不到的手型。",
                    "Leave it ticked. Unticking keeps every record — complete motion, but possibly shapes a human hand could not make.",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "取消勾选 速度限制剔除 / Speed limit culling 时，这一项下面的说明文字会换成作者的一句玩笑话（不装了，老子是机器人），意思就是不再按人的手速上限剔除记录。",
                "Untick 速度限制剔除 / Speed limit culling and the hint under it changes to the author's little joke (不装了，老子是机器人 / “No more pretending — I'm a robot”), meaning no records get culled by the human hand-speed limit any more.",
              ),
            },
          ],
        },
        {
          id: "avatar-tune-by-symptom",
          title: l("按现象找人：该动哪个参数", "Tuning by symptom"),
          blocks: [
            {
              kind: "p",
              text: l(
                "动作哪里不对劲，先对上现象，再去动对应的那一项；一次只改一个，改完重跑 🎹 2. 生成动画 / Generate Animation 看一眼，比较容易判断到底有没有变好。",
                "When the motion looks off, match the symptom to the parameter and change one thing at a time — rerun 🎹 2. 生成动画 / Generate Animation and look again, so you can tell whether it actually helped.",
              ),
            },
            {
              kind: "table",
              head: [
                l("看到的现象", "Symptom"),
                l("先动这个", "Reach for this"),
              ],
              rows: [
                [
                  l(
                    "手抬得太高，像在敲鼓。",
                    "The hands lift too high, as if drumming.",
                  ),
                  l(
                    "把 抬掌幅度 / Hand lift amplitude 往 0.8 左右调。",
                    "Bring 抬掌幅度 / Hand lift amplitude down towards 0.8.",
                  ),
                ],
                [
                  l(
                    "手像粘在琴键上，几乎不抬。",
                    "The hands look glued to the keys and barely lift.",
                  ),
                  l(
                    "把 抬掌幅度 / Hand lift amplitude 往上调，不超过 1.3。",
                    "Raise 抬掌幅度 / Hand lift amplitude, staying under 1.3.",
                  ),
                ],
                [
                  l(
                    "手在键盘上「瞬移」，不像人能弹出来。",
                    "The hands teleport across the keyboard — no human could play that.",
                  ),
                  l(
                    "把 手掌八度移动耗时 / Octave move duration 调大，同时保持 速度限制剔除 / Speed limit culling 勾选。",
                    "Raise 手掌八度移动耗时 / Octave move duration and keep 速度限制剔除 / Speed limit culling ticked.",
                  ),
                ],
                [
                  l(
                    "身体频繁左右转，看着晃。",
                    "The body keeps turning left and right.",
                  ),
                  l(
                    "把 转向敏感度 / Steering sensitivity 调低。",
                    "Lower 转向敏感度 / Steering sensitivity.",
                  ),
                ],
                [
                  l(
                    "整段高音区，身体却一直朝低音那边。",
                    "The passage sits high but the body keeps facing the low end.",
                  ),
                  l(
                    "把 转向敏感度 / Steering sensitivity 调高。",
                    "Raise 转向敏感度 / Steering sensitivity.",
                  ),
                ],
                [
                  l(
                    "长休止时手一动不动，很僵。",
                    "The hands freeze during long rests.",
                  ),
                  l(
                    "把 最大过渡时长 / Max transition duration 调大。",
                    "Raise 最大过渡时长 / Max transition duration.",
                  ),
                ],
                [
                  l(
                    "手指落键看起来慢半拍。",
                    "Fingers seem to land a beat late.",
                  ),
                  l(
                    "把 按键耗时 / Key press duration 调小一点。",
                    "Lower 按键耗时 / Key press duration a little.",
                  ),
                ],
              ],
            },
          ],
        },
      ],
    },

    // ═════════════════════════════════════════════════════════════
    // 页签 2：MIDI 处理
    // ═════════════════════════════════════════════════════════════
    {
      id: "tab-midi",
      title: l("MIDI 处理：决定「弹什么」", "MIDI tab: what gets played"),
      image: { image: 1 },
      blocks: [
        {
          kind: "p",
          text: l(
            "左栏管「用哪个文件、怎么解析」，右栏管「生成哪些轨」。左栏从上到下是 01 MIDI 文件管理 / MIDI File Management 和 02 参数设置 / Parameter Settings；右栏是 03 轨道选择 / Track Selection，它下面还会出现一条消息条。",
            "The left column decides which file is used and how it is parsed; the right column decides which tracks get animated. From the top, the left holds 01 MIDI 文件管理 / MIDI File Management and 02 参数设置 / Parameter Settings, while the right holds 03 轨道选择 / Track Selection with a message strip underneath it.",
          ),
        },
      ],
      subs: [
        {
          id: "midi-file",
          title: l(
            "01 MIDI 文件管理：选文件与扫描",
            "01 MIDI File Management: load and scan",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "这张卡片只干一件事：告诉软件这次分析哪份 MIDI。四个控件从上到下依次是当前文件、选文件、扫描、拖放区。",
                "This card does one job: tell the app which MIDI to analyse. Its four controls, top to bottom, are the current file, the picker, the scanner and the drop area.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "当前文件: / Current file: 后面跟着已选文件的**文件名**（不显示完整路径）。还没选时这里显示 未选择 MIDI 文件 / No MIDI file selected 和一行提示。",
                  "当前文件: / Current file is followed by the **file name** of the loaded MIDI (not the full path). Before you pick one it shows 未选择 MIDI 文件 / No MIDI file selected with a hint line.",
                ),
                l(
                  "📂 选择 MIDI 文件 / Select MIDI File 打开系统文件对话框，只筛 .mid 和 .midi。选中的文件会立刻替换掉当前文件。",
                  "📂 选择 MIDI 文件 / Select MIDI File opens the system file dialog filtered to .mid and .midi. The file you pick replaces the current one immediately.",
                ),
                l(
                  "🔍 扫描 MIDI 信息 / Scan MIDI Info 解析这份文件，并把轨道信息填到右栏的 轨道选择 / Track Selection 表里。没选文件就点，只会得到一条「请先选择 MIDI 文件」。",
                  "🔍 扫描 MIDI 信息 / Scan MIDI Info parses the file and fills the 轨道选择 / Track Selection table on the right. Click it with no file selected and you only get “pick a MIDI file first”.",
                ),
                l(
                  "底部虚线区 📥 将 MIDI 文件拖放到此处 / Drop MIDI file here（副标题 支持 .mid 和 .midi 格式 / Supports .mid and .midi formats）是拖放入口：把文件从资源管理器拖进窗口**任意位置**都行，效果和点 📂 选择 MIDI 文件 / Select MIDI File 一样。",
                  "The dashed 📥 将 MIDI 文件拖放到此处 / Drop MIDI file here area at the bottom (subtitled 支持 .mid 和 .midi 格式 / Supports .mid and .midi formats) accepts drag and drop: drop the file anywhere on the window and it works like 📂 选择 MIDI 文件 / Select MIDI File.",
                ),
              ],
            },
            {
              kind: "table",
              head: [
                l("屏幕上会出现", "What you may see"),
                l("为什么", "Why"),
                l("怎么办", "Do this"),
              ],
              rows: [
                [
                  l(
                    "「文件扩展名不正确…」「选择的文件没有扩展名」",
                    "“wrong file extension” / “the file has no extension”",
                  ),
                  l(
                    "选的文件不是 .mid / .midi，或者文件名没有扩展名。",
                    "The file is not .mid / .midi, or it has no extension at all.",
                  ),
                  l(
                    "当前文件不会被替换，重新选一份。",
                    "The current file is left untouched — pick another one.",
                  ),
                ],
                [
                  l(
                    "「MIDI 文件不存在或格式错误」",
                    "“MIDI file missing or malformed”",
                  ),
                  l(
                    "文件被删了、路径失效，或者内容不是有效的 MIDI。",
                    "The file was deleted, the path broke, or the content is not a valid MIDI.",
                  ),
                  l(
                    "换一份能正常播放的 MIDI。",
                    "Try a MIDI that plays normally elsewhere.",
                  ),
                ],
                [
                  l("「请先选择 MIDI 文件」", "“select a MIDI file first”"),
                  l(
                    "还没选文件就点了 🔍 扫描 MIDI 信息 / Scan MIDI Info（或者后面的预处理按钮）。",
                    "You clicked 🔍 扫描 MIDI 信息 / Scan MIDI Info (or a preprocessing button) before loading a file.",
                  ),
                  l("先选文件，再扫描。", "Load a file first, then scan."),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "扫描只是「读一遍」，既不改动你的 MIDI，也不改变当前文件，只是把轨道表填出来。",
                "Scanning is read-only: it leaves your MIDI and the current file untouched and simply fills in the track table.",
              ),
            },
          ],
        },
        {
          id: "midi-params",
          title: l(
            "02 参数设置：三个参数 + 轨道回显",
            "02 Parameter Settings: three parameters and the track echo",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "三个参数决定「怎么解析这份 MIDI」，卡片下半部分是已选轨道的回显。它们都会影响后面生成出来的动作，但对大多数曲子默认值就够。",
                "The three parameters decide how the MIDI is interpreted; the lower half of the card echoes the tracks you have selected. They all shape the motion that comes out, though the defaults suit most pieces.",
              ),
            },
            {
              kind: "table",
              head: [
                l("参数", "Parameter"),
                l("作用", "What it controls"),
                l("范围", "Range"),
                l("默认", "Default"),
                l("什么时候调", "When to change it"),
              ],
              rows: [
                [
                  l("使用通道", "使用通道 / Use channel"),
                  l(
                    "只处理某一个 MIDI 通道；-1 表示使用所有通道。",
                    "Process a single MIDI channel only; -1 means all channels.",
                  ),
                  l("任意通道号，-1 为全部", "Any channel number, -1 for all"),
                  l("-1", "-1"),
                  l(
                    "一份 MIDI 把多个声部塞进不同通道、你只想取其中一个时改成对应通道号；不确定就保持 -1，配合右边的轨道勾选来收窄范围。",
                    "Set a channel number when one MIDI spreads several parts across channels and you only want one of them; if unsure, leave -1 and narrow things down with track selection on the right instead.",
                  ),
                ],
                [
                  l("FPS", "FPS"),
                  l(
                    "生成动画用的帧率。它直接决定音符播放的速度，也决定总帧数。",
                    "The frame rate used for the animation. It sets both the playback speed and the total frame count.",
                  ),
                  l("整数", "Integer"),
                  l("60", "60"),
                  l(
                    "和你的目标工程对齐最省事（Blender / Unreal 常用 30 或 60）。调小等于放慢、帧数变少。",
                    "Matching your target project saves trouble (30 or 60 is common in Blender / Unreal). Lowering it slows playback and reduces the frame count.",
                  ),
                ],
                [
                  l("音符量化", "音符量化 / Note quantization"),
                  l(
                    "把时间上很接近的音符归到同一格，让动作更规整。",
                    "Groups notes that land very close together onto the same grid so the motion reads cleaner.",
                  ),
                  l(
                    "无量化 / 1/16 / 1/32 / 1/64 / 1/128",
                    "无量化 / none, 1/16 / 1/32 / 1/64 / 1/128",
                  ),
                  l("1/16", "1/16"),
                  l(
                    "真人演奏的 MIDI 常有几毫秒的抖动，1/16 通常能让画面干净；打谱软件导出、本身就很整齐的 MIDI 可以选 无量化。",
                    "A human performance carries a few milliseconds of jitter, and 1/16 usually cleans that up; a neatly exported notation MIDI can use 无量化 / none.",
                  ),
                ],
              ],
            },
            {
              kind: "p",
              text: l(
                "卡片下半部分是 已选择的轨道: / Selected tracks: 加上轨道号（例如 轨道 2），一条都没选时显示 无轨道 / No tracks 和一句 请在右侧选择轨道后添加。——这句提示就是在催你去右边勾完记得点添加。",
                "The lower half of the card shows 已选择的轨道: / Selected tracks followed by the track numbers (e.g. 轨道 2). With nothing selected it shows 无轨道 / No tracks plus 请在右侧选择轨道后添加。, which is the app's way of telling you to tick tracks on the right and then add them.",
              ),
            },
          ],
        },
        {
          id: "midi-tracks",
          title: l(
            "03 轨道选择：勾出要生成动画的轨",
            "03 Track Selection: tick what to animate",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "点过 🔍 扫描 MIDI 信息 / Scan MIDI Info 之后，这张表列出这份 MIDI 里**所有含音符的轨道**；一个音符都没有的轨道不会出现在表里。",
                "After you click 🔍 扫描 MIDI 信息 / Scan MIDI Info, this table lists **every track that contains notes**; tracks with no notes never appear.",
              ),
            },
            {
              kind: "table",
              head: [
                l("列", "Column"),
                l("含义", "Meaning"),
                l("怎么用", "How to use it"),
              ],
              rows: [
                [
                  l("选择", "选择 / Select"),
                  l(
                    "勾选框。勾上的行才会被 ✅ 添加选中的轨道 / Add Selected Tracks 收进去。",
                    "A checkbox. Only ticked rows are picked up by ✅ 添加选中的轨道 / Add Selected Tracks.",
                  ),
                  l(
                    "只勾真正要演奏的那条轨，通常是钢琴那条。",
                    "Tick only the track that should be performed — usually the piano one.",
                  ),
                ],
                [
                  l("轨道", "轨道 / Track"),
                  l(
                    "轨道序号，也是输出文件名和日志里出现的那个号。",
                    "The track index, which also appears in output file names and in the console log.",
                  ),
                  l(
                    "记下来，便于在 📋 记录查看 / Records 里对照。",
                    "Note it down so you can cross-check it in 📋 记录查看 / Records.",
                  ),
                ],
                [
                  l("通道", "通道 / Channel"),
                  l(
                    "这条轨主要使用的 MIDI 通道，识别不出来时显示 N/A。",
                    "The MIDI channel the track mostly uses, or N/A when it cannot be told.",
                  ),
                  l(
                    "配合 使用通道 / Use channel 判断要不要收窄。",
                    "Use it together with 使用通道 / Use channel to decide whether to narrow anything.",
                  ),
                ],
                [
                  l("乐器", "乐器 / Instrument"),
                  l(
                    "从轨道事件里推断出来的乐器名，例如 Acoustic Grand Piano。",
                    "The instrument name inferred from the track's events, e.g. Acoustic Grand Piano.",
                  ),
                  l(
                    "用它确认这条轨是不是钢琴。",
                    "Use it to confirm the track really is a piano.",
                  ),
                ],
                [
                  l("音符数", "音符数 / Notes"),
                  l(
                    "这条轨一共多少个音符。",
                    "Total note count on that track.",
                  ),
                  l(
                    "数字特别小通常说明选错了行。",
                    "An unusually small number usually means the wrong row.",
                  ),
                ],
              ],
            },
            {
              kind: "ol",
              items: [
                l(
                  "在目标轨道前面打勾，可以勾多条。",
                  "Tick the target track — or several.",
                ),
                l(
                  "点 ✅ 添加选中的轨道 / Add Selected Tracks，左栏 已选择的轨道: / Selected tracks: 会立刻列出结果，同时下方弹出 轨道添加成功 / Tracks added successfully。",
                  "Click ✅ 添加选中的轨道 / Add Selected Tracks; 已选择的轨道: / Selected tracks: on the left updates immediately and a 轨道添加成功 / Tracks added successfully message appears below.",
                ),
                l(
                  "忘了点这一步，执行时会看到「请先选择至少一个轨道」。",
                  "Skip this click and generation later complains that at least one track must be selected.",
                ),
              ],
            },
            {
              kind: "callout",
              tone: "warn",
              title: l(
                "它是整体替换，不是追加",
                "It replaces, it does not append",
              ),
              text: l(
                "✅ 添加选中的轨道 / Add Selected Tracks 会先清空原来的选择，再写入这次勾选的结果。所以想「在原来的基础上再加一条」，得把原来的那条一起勾上再点。",
                "✅ 添加选中的轨道 / Add Selected Tracks clears the previous selection first, then writes the currently ticked rows. So to add one track to an existing selection, tick the old ones as well before clicking.",
              ),
            },
            {
              kind: "note",
              text: l(
                "还没扫描过时，这张卡片显示 暂无轨道信息 和 请先在左侧扫描 MIDI 文件以查看轨道信息。 / Scan the MIDI file on the left to view track info.",
                "Before any scan, the card reads 暂无轨道信息 / No track info with the hint 请先在左侧扫描 MIDI 文件以查看轨道信息。 / Scan the MIDI file on the left to view track info.",
              ),
            },
          ],
        },
        {
          id: "midi-tools",
          title: l(
            "修剪、均一化与消息条",
            "Trim, tempo normalize and the message strip",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "轨道选择 / Track Selection 卡片底部还有两个预处理按钮。它们都**另存为新文件**，不动你的原文件，也不会自动把软件里的当前文件换成处理后的新文件。",
                "Two preprocessing buttons sit at the bottom of the 轨道选择 / Track Selection card. Both **write a new file**, leave your original alone, and do not swap the app's current file for the result.",
              ),
            },
            {
              kind: "table",
              head: [
                l("按钮", "Button"),
                l("做什么", "What it does"),
                l("输出", "Output"),
              ],
              rows: [
                [
                  l(
                    "✂️ 修剪并延长 MIDI 音符",
                    "✂️ 修剪并延长 MIDI 音符 / Trim & Extend MIDI Notes",
                  ),
                  l(
                    "按固定的间隔阈值（960 ticks）把被切断的长音修剪并延长，免得一个长音被拆成很多小段，导致动作碎。**它要求先选好轨道**，否则会提示「请先选择至少一个轨道」。",
                    "Uses a fixed gap threshold (960 ticks) to trim and extend fragmented notes so a held note does not break into many pieces and chop up the motion. **It requires a selected track** — otherwise it asks you to select at least one.",
                  ),
                  l(
                    "与源文件同目录，文件名是 原名_trimmed 加原来的扩展名",
                    "<name>_trimmed plus the original extension, next to the source file",
                  ),
                ],
                [
                  l(
                    "🎯 速度均一化（全轨道）",
                    "🎯 速度均一化（全轨道） / Tempo Normalize (All Tracks)",
                  ),
                  l(
                    "把全曲所有的速度变化抹平成单一速度，消掉渐快渐慢带来的抖动。名字里的「全轨道」是字面意思：它作用于整份 MIDI，而不只是你勾选的那条轨。",
                    "Flattens every tempo change in the piece into one tempo, removing the jitter that accelerando and ritardando introduce. “All Tracks” is literal: it applies to the whole MIDI, not just the ticked track.",
                  ),
                  l(
                    "与源文件同目录，文件名是 原名_tempo_normalized 加原来的扩展名",
                    "<name>_tempo_normalized plus the original extension, next to the source file",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "warn",
              title: l(
                "处理完记得把新文件选进来",
                "Load the new file afterwards",
              ),
              text: l(
                "这两个按钮只负责生成新文件。想用处理后的结果生成动画，要回到上面重新 📂 选择 MIDI 文件 / Select MIDI File 选中新文件，再 🔍 扫描 MIDI 信息 / Scan MIDI Info、重新 ✅ 添加选中的轨道 / Add Selected Tracks。",
                "These buttons only produce new files. To animate the processed result, go back up, 📂 选择 MIDI 文件 / Select MIDI File the new file, 🔍 扫描 MIDI 信息 / Scan MIDI Info again, and ✅ 添加选中的轨道 / Add Selected Tracks once more.",
              ),
            },
            {
              kind: "note",
              text: l(
                "两个按钮跑完都会在右栏下方弹一条绿色提示，写着输出文件名；轨道添加成功 / Tracks added successfully 也走同一条消息条。这些提示会在一小会儿后自动隐去，不用手动关。",
                "Both buttons report back with a green message under the right column naming the output file, and 轨道添加成功 / Tracks added successfully uses the same strip. These messages fade out on their own after a moment.",
              ),
            },
          ],
        },
      ],
    },

    // ═════════════════════════════════════════════════════════════
    // 页签 3：执行操作
    // ═════════════════════════════════════════════════════════════
    {
      id: "tab-execute",
      title: l("执行操作：生成与日志", "Execute tab: generation and the log"),
      image: { image: 2 },
      blocks: [
        {
          kind: "p",
          text: l(
            "这一页左栏自上而下是 01 角色概览 / Avatar Overview、02 MIDI 参数 / MIDI Parameters、03 执行操作 / Operations，右栏整列是 04 控制台输出 / Console Output。**上面两张卡片只是回显**，页面上没有任何输入框能改角色或 MIDI。",
            "The left column stacks 01 角色概览 / Avatar Overview, 02 MIDI 参数 / MIDI Parameters and 03 执行操作 / Operations; the right column is entirely 04 控制台输出 / Console Output. The top two cards are **read-only echoes** — nothing on this page edits the performer or the MIDI.",
          ),
        },
      ],
      subs: [
        {
          id: "execute-echo",
          title: l(
            "01 / 02 执行前先核对回显",
            "01 / 02 Review the echoes first",
          ),
          blocks: [
            {
              kind: "table",
              head: [
                l("卡片", "Card"),
                l("显示什么", "What it shows"),
                l("空的时候", "When it is empty"),
              ],
              rows: [
                [
                  l("角色概览", "角色概览 / Avatar Overview"),
                  l(
                    "当前角色 / Current Avatar（角色名）、单手音域 / Single-hand range（一个数字，就是角色的 单手跨度 / Hand span）、转向敏感度 / Steering sensitivity。",
                    "当前角色 / Current Avatar (the performer name), 单手音域 / Single-hand range (a number — the performer's 单手跨度 / Hand span) and 转向敏感度 / Steering sensitivity.",
                  ),
                  l(
                    "未选择角色 / No avatar selected 加一句 请先在角色设置页选择角色文件。",
                    "未选择角色 / No avatar selected, plus a hint to pick an avatar file on the Avatar tab first.",
                  ),
                ],
                [
                  l("MIDI 参数", "MIDI 参数 / MIDI Parameters"),
                  l(
                    "当前 MIDI / Current MIDI（文件名 + 完整路径）、轨道 / Track（一个列表，例如 [2]）、通道 / Channel、帧率 / Frame rate。",
                    "当前 MIDI / Current MIDI (file name plus full path), 轨道 / Track (a list, e.g. [2]), 通道 / Channel and 帧率 / Frame rate.",
                  ),
                  l(
                    "未选择 MIDI 文件 / No MIDI file selected 加一句 请先在 MIDI 处理页完成文件选择与轨道筛选。",
                    "未选择 MIDI 文件 / No MIDI file selected, plus a hint to pick a file and filter tracks on the MIDI tab first.",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "warn",
              text: l(
                "这里显示什么，待会儿就按什么生成。看到 未选择角色 / No avatar selected 或 未选择 MIDI 文件 / No MIDI file selected 就别急着点生成——回对应页签补齐，回来这张卡片会自己更新。",
                "Whatever shows here is what generation will use. If you see 未选择角色 / No avatar selected or 未选择 MIDI 文件 / No MIDI file selected, do not rush into generating — go back to the matching tab, fix it, and this card updates itself.",
              ),
            },
          ],
        },
        {
          id: "execute-run",
          title: l(
            "03 执行操作：分步还是一键",
            "03 Operations: stepwise or one click",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "两张小卡片给出两种跑法：分步执行 / Step by Step 把过程拆成两步，快速执行 / Quick Run 一次串完。第一次跑新素材建议走分步，出问题时能立刻知道是「指法算得不对」还是「动画阶段出错」。",
                "The two small cards offer two ways to run it: 分步执行 / Step by Step splits the work in two, 快速执行 / Quick Run chains it all. On new material, go stepwise first — then you immediately know whether a problem is in the fingering or in the animation stage.",
              ),
            },
            {
              kind: "table",
              head: [
                l("卡片", "Card"),
                l("按钮", "Button"),
                l("点了会发生什么", "What it does"),
                l("什么时候用", "When to use it"),
              ],
              rows: [
                [
                  l("分步执行", "分步执行 / Step by Step"),
                  l("🎼 1. 生成指法", "🎼 1. 生成指法 / Generate Fingering"),
                  l(
                    "初始化系统（钢琴 + 左右手）并逐帧计算指法，结果写进 output/hand_recorders/ 下的 .hand 手部记录。",
                    "Initializes the system (piano plus both hands), computes fingering frame by frame, and writes .hand hand records under output/hand_recorders/.",
                  ),
                  l(
                    "第一次跑新素材；或指法本身有问题、需要单独排查时。",
                    "First run on new material, or when the fingering itself needs isolated debugging.",
                  ),
                ],
                [
                  l("分步执行", "分步执行 / Step by Step"),
                  l("🎹 2. 生成动画", "🎹 2. 生成动画 / Generate Animation"),
                  l(
                    "读取上一步的 .hand，算出手掌与手指动画，写出全部结果文件。",
                    "Reads the .hand from step one, computes palm and finger animation, and writes every result file.",
                  ),
                  l(
                    "指法已经确认、只改了动作参数时，只重跑这一步就够。",
                    "Once the fingering looks right and you only changed motion parameters, rerun just this step.",
                  ),
                ],
                [
                  l("快速执行", "快速执行 / Quick Run"),
                  l("⚡ 一键执行", "⚡ 一键执行 / Run All"),
                  l(
                    "把初始化 → 指法 → 动画按 1/3、2/3、3/3 的顺序一次跑完。",
                    "Runs initialization → fingering → animation in one go, logging 1/3, 2/3, 3/3.",
                  ),
                  l(
                    "素材和参数都确认过了，只想尽快出结果。",
                    "Assets and parameters are settled and you just want output.",
                  ),
                ],
              ],
            },
            {
              kind: "callout",
              tone: "tip",
              text: l(
                "卡片上写着 建议先生成指法，再生成动画，方便定位中间步骤的问题。 跳过第一步直接点 🎹 2. 生成动画 / Generate Animation，会得到「指法文件不存在，请先生成指法」。",
                "The card says: generate fingering first, then animation, to make intermediate problems easier to locate. Skip step one and 🎹 2. 生成动画 / Generate Animation answers with “the fingering file does not exist — generate fingering first”.",
              ),
            },
            {
              kind: "note",
              text: l(
                "运行中按钮下方会出现转圈和 正在执行... / Running...，旁边有 取消 / Cancel。取消会停下当前任务，控制台留下「执行已取消」，已经写出的中间文件保留。",
                "While it runs, a spinner and 正在执行... / Running... appear below the buttons with 取消 / Cancel beside them. Cancelling stops the current task, logs “execution cancelled”, and keeps whatever intermediate files were already written.",
              ),
            },
          ],
        },
        {
          id: "execute-console",
          title: l(
            "04 控制台输出：怎么知道跑成功了",
            "04 Console Output: reading the log",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "右栏整列是 控制台输出 / Console Output：三个按钮、两个徽标、一块持续刷新的日志区。",
                "The whole right column is 控制台输出 / Console Output: three buttons, two badges, and a log area that keeps refreshing.",
              ),
            },
            {
              kind: "table",
              head: [l("控件", "Control"), l("作用", "What it does")],
              rows: [
                [
                  l("复制结果", "复制结果 / Copy Result"),
                  l(
                    "把最近一次成功生成的结果路径复制到剪贴板；还没生成过会提示「尚未找到可复制的结果路径」。",
                    "Copies the path of the most recent successful result; before any run it says “no result path to copy yet”.",
                  ),
                ],
                [
                  l("复制全部", "复制全部 / Copy All"),
                  l(
                    "把整段日志复制到剪贴板。找人帮忙排查时贴这个，比截图有用得多——日志里带着完整路径和错误原文。",
                    "Copies the entire log. When asking for help, paste this instead of a screenshot — it carries the full paths and the original error text.",
                  ),
                ],
                [
                  l("清空信息", "清空信息 / Clear"),
                  l(
                    "只清空显示，不影响任何已经生成出来的文件。",
                    "Clears the display only; no generated file is affected.",
                  ),
                ],
                [
                  l(
                    "状态：空闲 / 状态：运行中",
                    "状态：空闲 / Status: Idle and 状态：运行中 / Status: Running",
                  ),
                  l(
                    "徽标。左边那个是日志条数，右边这个告诉你程序还在不在算。",
                    "Badges. The left one counts log entries; the right one tells you whether the app is still working.",
                  ),
                ],
              ],
            },
            {
              kind: "p",
              text: l(
                "一次成功的一键执行，日志大概长这样：1/3 正在初始化系统... / 1/3 Initializing system... → 系统初始化完成 / System initialization complete → 2/3 正在生成指法... / 2/3 Generating fingering... → 指法生成完成 / Fingering generation complete → 3/3 正在生成动画... / 3/3 Generating animation... → 动画生成完成 / Animation generation complete → 三行数据文件路径 → KeyRipple配置文件已保存到: … → 一键执行结束 / One-click operation finished。分步执行时没有 N/3 前缀，其余一样。",
                "A successful one-click run logs roughly this: 1/3 正在初始化系统... / 1/3 Initializing system... → 系统初始化完成 / System initialization complete → 2/3 正在生成指法... / 2/3 Generating fingering... → 指法生成完成 / Fingering generation complete → 3/3 正在生成动画... / 3/3 Generating animation... → 动画生成完成 / Animation generation complete → the paths of the data files → the saved result path → 一键执行结束 / One-click operation finished. Stepwise runs have no N/3 prefix but are otherwise the same.",
              ),
            },
            {
              kind: "p",
              text: l(
                "指法阶段会先把这份 MIDI 的统计打出来：全曲的速度变化、每拍多少 tick、按当前 FPS 每拍约多少帧、和弦 / 单音符总数、按下的音符总数与其中不重复的个数，以及换算成当前 FPS 后一共多少帧、大约多少秒。**这些数字是确认「文件选对了没有」最快的办法**——如果总时长和你印象里的曲子差得离谱，多半是文件或轨道选错了。",
                "The fingering stage first prints statistics for the MIDI: tempo changes, ticks per beat, frames per beat at the current FPS, total chords / single notes, total notes pressed and how many are unique, plus the total frame count and duration at the current FPS. **These numbers are the fastest way to confirm you picked the right file** — if the duration is wildly off from what you expect, the file or the track is probably wrong.",
              ),
            },
            {
              kind: "callout",
              tone: "warn",
              title: l(
                "看到「剔除的记录」数字很大怎么办",
                "When the culled-record count looks large",
              ),
              text: l(
                "指法生成完会有一行「因超出移动速度上限而剔除的记录 左手 N 条 / 右手 M 条；涉及帧 X」。数字明显偏大时，多半是手速上限太紧：回角色页把 手掌八度移动耗时 / Octave move duration 调大，或者取消勾选 速度限制剔除 / Speed limit culling 让记录全部留下——后者动作完整，但可能出现人手做不到的手型。",
                "After fingering finishes you get a line counting the records culled for exceeding the hand-movement speed limit, split by hand, with the frames involved. If that count is large the speed ceiling is probably too tight: raise 手掌八度移动耗时 / Octave move duration on the Avatar tab, or untick 速度限制剔除 / Speed limit culling to keep every record — complete motion, but possibly shapes a human hand could not make.",
              ),
            },
            {
              kind: "note",
              text: l(
                "日志是持续追加的，越跑越长。用 清空信息 / Clear 清一下，下一轮的输出会从干净的窗口重新开始。",
                "The log keeps appending, so it grows with every run. 清空信息 / Clear gives you a clean window for the next one.",
              ),
            },
          ],
        },
      ],
    },

    // ═════════════════════════════════════════════════════════════
    // 页签 4：记录查看
    // ═════════════════════════════════════════════════════════════
    {
      id: "tab-records",
      title: l(
        "记录查看：逐帧排查指法",
        "Records tab: debugging fingering frame by frame",
      ),
      image: { image: 3 },
      blocks: [
        {
          kind: "p",
          text: l(
            "📋 记录查看 / Records 是一个只读的排查工具，标题写着 Hand Recorder 查看器 / Hand Recorder Viewer、副标题 浏览和搜索钢琴演奏手部记录 / Browse and search piano performance hand records。它把生成出来的手部记录摊开，让你看每一帧、每根手指被分到了哪个音。它不参与生成，跑通流程可以完全跳过。",
            "📋 记录查看 / Records is a read-only debugging tool titled Hand Recorder 查看器 / Hand Recorder Viewer with the subtitle 浏览和搜索钢琴演奏手部记录 / Browse and search piano performance hand records. It lays out the generated hand records so you can inspect which note each finger was given on any frame. It takes no part in generation and can be skipped entirely.",
          ),
        },
        {
          kind: "p",
          text: l(
            "文件来源是固定的：软件按你当前选的 MIDI 与轨道去找 output/hand_recorders/{midi文件名}_{轨道}.hand。所以**必须先在 🎵 MIDI处理 里选好文件与轨道、并且至少生成过一次**，这一页才有东西可看。第一次切到这一页时，程序会自动把窗口最大化一次——谱面需要横向空间。",
            "The source file is fixed: the app looks for output/hand_recorders/{midi-file-name}_{track}.hand based on the MIDI and track currently selected. So you **must** have selected the file and track on 🎵 MIDI处理 and run at least one generation before there is anything to see here. The first time you open this tab the app maximises the window once, because the score needs horizontal room.",
          ),
        },
        {
          kind: "callout",
          tone: "info",
          text: l(
            "还没准备好时，这一页会直接告诉你缺什么：没选 MIDI / 轨道是 请先在 MIDI 处理面板选择 MIDI 文件和轨道，再切换到本面板 / Select the MIDI file and tracks in the MIDI panel before switching to this panel；选好了但文件读不到是 无法加载 .hand 文件，请确认 MIDI 文件和轨道已经执行过生成 / Failed to load the .hand file. Make sure the MIDI file and tracks have been processed。",
            "When it is not ready, the page says exactly what is missing: with no MIDI / track chosen you get 请先在 MIDI 处理面板选择 MIDI 文件和轨道，再切换到本面板 / Select the MIDI file and tracks in the MIDI panel before switching to this panel; with a file that cannot be read you get 无法加载 .hand 文件，请确认 MIDI 文件和轨道已经执行过生成 / Failed to load the .hand file. Make sure the MIDI file and tracks have been processed.",
          ),
        },
      ],
      subs: [
        {
          id: "records-cards",
          title: l(
            "顶部两张卡片：文件信息与搜索筛选",
            "Top cards: file info and filters",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "最上面一行左边是 收起顶部卡片 / 展开顶部卡片，用来把下面两张卡片折叠起来，把纵向空间让给谱面；右边 文件: 后面是当前加载的 .hand 路径。",
                "The top row has 收起顶部卡片 / Collapse cards / 展开顶部卡片 / Expand cards on the left to collapse the two cards below and give the vertical space to the score, and 文件: / File with the currently loaded .hand path on the right.",
              ),
            },
            {
              kind: "table",
              head: [
                l("卡片", "Card"),
                l("里面有什么", "What is inside"),
                l("空的时候", "When empty"),
              ],
              rows: [
                [
                  l("📄 文件信息", "📄 文件信息 / File info"),
                  l(
                    "文件: / File:（.hand 的完整路径）、记录数: / Records:（N 条）、区间: / Range:（这份记录覆盖的帧范围，例如 帧 0 - 6492）。",
                    "文件: / File: (the full .hand path), 记录数: / Records: (N 条 / N records) and 区间: / Range: (the frame range covered, e.g. 帧 0 - 6492).",
                  ),
                  l(
                    "文件: / File: 显示 未加载，记录数: / Records: 显示一个短横线。",
                    "文件: / File: reads 未加载 / Not loaded and 记录数: / Records: shows a dash.",
                  ),
                ],
                [
                  l("🔍 搜索与筛选", "🔍 搜索与筛选 / Search & filters"),
                  l(
                    "帧数: / Frame: 输入框（占位提示 留空=全局）加一栏 ± 数值，决定以这一帧为中心搜索的窗口宽度；下面 手指筛选 勾上后出现五个下拉；右侧是 🔍 搜索 / 🔄 刷新 / ✕ 清除。",
                    "A 帧数: / Frame box (placeholder 留空=全局 / empty = all) plus a ± value that sets how wide the search window around that frame is; below it, ticking 手指筛选 reveals five dropdowns; on the right sit 🔍 搜索 / 🔄 刷新 / ✕ 清除.",
                  ),
                  l(
                    "窗口窄的时候三个按钮会自动缩成图标。",
                    "On a narrow window the three buttons collapse into icons automatically.",
                  ),
                ],
              ],
            },
            {
              kind: "ul",
              items: [
                l(
                  "帧数: / Frame: 留空就是整份记录；填了帧号再配 ±（默认 10 帧，可调 0 – 200）就是「这一帧前后一小段」。",
                  "Leaving 帧数: / Frame: empty covers the whole record; typing a frame number together with ± (10 frames by default, adjustable 0 – 200) narrows it to a small window around that frame.",
                ),
                l(
                  "手指筛选 勾上以后有五个下拉：按下、交叉、保留、黑键 各自可以选 任意 / 是 / 否，最后一个是手别 任意 / 左手 / 右手。",
                  "With 手指筛选 ticked you get five dropdowns: 按下, 交叉, 保留 and 黑键 each offer 任意 / 是 / 否, and the last one picks the hand — 任意 / 左手 / 右手.",
                ),
                l(
                  "🔍 搜索 按当前条件筛；🔄 刷新 重新从磁盘读一遍 .hand（刚重新生成过就用它）；✕ 清除 把帧号、筛选条件和选中状态一次清空。",
                  "🔍 搜索 applies the current filters; 🔄 刷新 re-reads the .hand from disk (use it right after regenerating); ✕ 清除 wipes the frame number, the filters and the selection in one go.",
                ),
              ],
            },
          ],
        },
        {
          id: "records-score",
          title: l("双手下落谱：看这一帧发生了什么", "Falling-note score"),
          blocks: [
            {
              kind: "p",
              text: l(
                "页面中间那张卡片是 双手下落谱。横轴是音高（整副键盘，不缩放不平移），纵轴是时间——越靠下越接近此刻；**最底下那排键盘本身就是播放头**，音符自上而下朝它落。右上角标着这副键盘的音域，例如「键盘：40 - C8」。",
                "The card in the middle is the 双手下落谱 / Falling-note score. The horizontal axis is pitch (the whole keyboard, no zooming or panning) and the vertical axis is time — the lower it is, the closer to now; **the keyboard strip at the bottom is the playhead itself**, and notes fall towards it. The top-right corner shows the keyboard range, e.g. “Keyboard: 40 - C8”.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "标题行给出一眼统计（例如「音符 1015  左手 428  右手 587  交叉指 1  保留指 558」）——两只手各占多少、有没有交叉指，看这里最快。",
                  "The title row gives the counts at a glance (e.g. “Notes 1015  Left 428  Right 587  Cross 1  Keep 558”) — the fastest way to see whether the two hands are balanced and whether any crossing happened.",
                ),
                l(
                  "图例：红色系是 左手，蓝色系是 右手；实色圆点是按键指，深色带白边的是交叉指，保留指只画淡色描边和指号（不填充）。",
                  "Legend: red is 左手 / Left and blue is 右手 / Right; a solid dot is a pressing finger, dark with a white rim is a crossed finger, and keep-pressed notes are drawn as a faint outline with the finger number only (no fill).",
                ),
                l(
                  "操作提示就写在谱面上方：纵轴=时间（上=未来、下=此刻），横轴=音高；点任意一个圆点会显示这个音符的详情（帧、左右手、第几指、音高、状态、是否跨指）。",
                  "The interaction hint sits right above the score: Y is time (up = future, down = now) and X is pitch; click any dot to see that note's details (frame, hand, finger, pitch, state, whether it crossed).",
                ),
              ],
            },
            {
              kind: "table",
              head: [l("控件", "Control"), l("作用", "What it does")],
              rows: [
                [
                  l("▶ 播放 / ⏸ 暂停", "▶ 播放 / Play / ⏸ 暂停 / Pause"),
                  l(
                    "从当前播放头位置开始走谱面，同时把音符发给 MIDI 输出设备，可以边看边听。",
                    "Plays the score from the current playhead and sends the notes to the MIDI output device, so you can watch and listen at the same time.",
                  ),
                ],
                [
                  l("⏹ 停止", "⏹ 停止 / Stop"),
                  l(
                    "停下并把播放头放回开头。",
                    "Stops and returns the playhead to the start.",
                  ),
                ],
                [
                  l("◀ / ▶", "◀ / ▶"),
                  l(
                    "单帧前后步进，用来卡在出问题的那一帧上细看。",
                    "Step one frame back or forward to park on the exact frame you care about.",
                  ),
                ],
                [
                  l(
                    "音符数 / 时长 / 时间 / 跨度",
                    "Note count / duration / time / span",
                  ),
                  l(
                    "播放条后面依次给出可播放的音符数与总时长、当前时间，以及当前视窗跨度多少帧。",
                    "After the transport you get the playable note count and total duration, the current time, and the current view span in frames.",
                  ),
                ],
                [
                  l("MIDI 输出设备下拉", "MIDI output device dropdown"),
                  l(
                    "选播放时把音符发到哪台设备，第一项是 自动。旁边的 🔄 刷新设备 重新枚举设备，🔔 测试音 发一个音确认能听到。",
                    "Chooses where notes go during playback; the first entry is 自动 / Auto. 🔄 刷新设备 re-enumerates the devices and 🔔 测试音 sends a test note to confirm you can hear it.",
                  ),
                ],
                [
                  l("复位", "复位 / Reset"),
                  l(
                    "把纵向跨度恢复到默认的 5 秒。缩放过的时侯它才会出现。",
                    "Restores the default 5-second vertical span. It only appears once you have zoomed.",
                  ),
                ],
              ],
            },
            {
              kind: "note",
              text: l(
                "缩放与定位：Ctrl + 滚轮调纵向跨度（0.5 秒 – 30 秒，默认 5 秒）；拖动右侧那条竖向缩略图可以整曲大范围定位；双击谱面或点 复位 回到 5 秒视野。如果 MIDI 设备测试不出声，提示会告诉你检查 Windows 默认播放设备和音量——midi 合成器走的是系统默认输出。",
                "Zoom and navigation: Ctrl + wheel changes the vertical span (0.5 s – 30 s, 5 s by default); dragging the vertical thumbnail on the right seeks across the whole piece; double-clicking the score or hitting 复位 returns to a 5-second view. If the test note stays silent, the hint tells you to check the Windows default playback device and volume — the MIDI synth uses the system default output.",
              ),
            },
          ],
        },
        {
          id: "records-results",
          title: l(
            "匹配结果：把可疑的帧挑出来",
            "Matches: pulling out the suspicious frames",
          ),
          blocks: [
            {
              kind: "p",
              text: l(
                "搜索之后，谱面下方会出现 匹配结果: 和一批记录卡片。每张卡片对应一帧：标题是帧号，左边给出这一帧的 中位（左右手中点）以及 左手、右手 各自的手位与跨度，右边是每只手一张小表，逐根手指列出 指 / 交叉 / 保留 / 键色（黑 或 白）/ 音高 / 按下。",
                "After a search, 匹配结果: / Matches and a set of record cards appear below the score. Each card is one frame: the title is the frame number, the left side gives that frame's 中位 / midpoint plus each hand's position and span, and the right side holds one small table per hand listing every finger's 指 / 交叉 / 保留 / 键色 (黑 or 白) / 音高 / 按下.",
              ),
            },
            {
              kind: "ul",
              items: [
                l(
                  "☐ 全选 / ✕ 取消全选 一次勾选或取消当前列表；📋 复制结果 把勾中的记录导出成文本到剪贴板，方便贴出来讨论。",
                  "☐ 全选 / ✕ 取消全选 selects or clears the whole list at once; 📋 复制结果 / Copy Result exports the ticked records as text to the clipboard so you can paste them somewhere.",
                ),
                l(
                  "匹配结果最多显示前 100 条，超出时会提示「匹配 N 条，仅显示前 100 条」。",
                  "Matches are capped at the first 100 rows, with a hint telling you how many matched in total.",
                ),
                l(
                  "没有匹配时显示 暂无匹配结果：还没设条件时提示「共 N 条记录，输入帧数或启用手指标签进行搜索」，设了条件仍为空则提示调整搜索条件。",
                  "With no matches you get 暂无匹配结果 / No matches — either a hint to enter a frame or enable finger filters, or, if filters are already on, a prompt to loosen them.",
                ),
              ],
            },
            {
              kind: "callout",
              tone: "tip",
              title: l("典型用法", "The typical workflow"),
              text: l(
                "动画里某一段手指明显穿模：先在 帧数: / Frame: 里输入大概的那一帧，勾上 手指筛选 把 按下 和 交叉 都设成 是，然后看点开的那几张卡片——当时每根手指被分到了哪个音、有没有出现交叉指，一眼就清楚。确认是分配问题，再回角色页调 手掌八度移动耗时 / Octave move duration 或 转向敏感度 / Steering sensitivity，重跑 🎹 2. 生成动画 / Generate Animation 对比。",
                "When fingers visibly clip during a passage: type the approximate frame into 帧数: / Frame:, tick 手指筛选 and set 按下 and 交叉 to 是, then read the cards that come up — which note each finger was assigned and whether a cross appeared is immediately visible. Once you have confirmed it is an assignment problem, go back to the Avatar tab, adjust 手掌八度移动耗时 / Octave move duration or 转向敏感度 / Steering sensitivity, and rerun 🎹 2. 生成动画 / Generate Animation to compare.",
              ),
            },
          ],
        },
      ],
    },

    // ═════════════════════════════════════════════════════════════
    // 默认值总表
    // ═════════════════════════════════════════════════════════════
    {
      id: "defaults",
      title: l("默认值总表：先别动，需要时再回来", "Defaults at a glance"),
      image: { image: 0 },
      blocks: [
        {
          kind: "p",
          text: l(
            "第一次用，这张表里的东西一个都不用改。它回答的是「哪些默认值可以放心用、哪些是迟早会想动的」。",
            "On a first run, nothing in this table needs changing. It answers two questions: which defaults are safe to keep, and which ones you will eventually want to touch.",
          ),
        },
        {
          kind: "table",
          head: [
            l("参数", "Parameter"),
            l("默认", "Default"),
            l("什么时候会想动它", "When you would change it"),
          ],
          rows: [
            [
              l("抬掌幅度", "抬掌幅度 / Hand lift amplitude"),
              l("1.0 x", "1.0 x"),
              l(
                "觉得抬手太夸张或太呆板时。",
                "When the lift looks exaggerated or too stiff.",
              ),
            ],
            [
              l("按键耗时", "按键耗时 / Key press duration"),
              l("0.0875 秒（显示为 0.087 s）", "0.0875 s (shown as 0.087 s)"),
              l(
                "快曲子嫌手指落下慢半拍时。",
                "When fingers seem to land a beat late on a fast piece.",
              ),
            ],
            [
              l("手掌八度移动耗时", "手掌八度移动耗时 / Octave move duration"),
              l("0.2 秒", "0.2 s"),
              l(
                "手在键盘上「瞬移」，或反过来动作太黏时。",
                "When the hands teleport across the keyboard, or the opposite — motion feels sticky.",
              ),
            ],
            [
              l("最大过渡时长", "最大过渡时长 / Max transition duration"),
              l("3.0 秒", "3.0 s"),
              l(
                "长休止处动作太空、或过渡太拖时。",
                "When long rests look empty, or transitions drag.",
              ),
            ],
            [
              l("抬指时间", "抬指时间 / Lift time"),
              l("0.12 秒", "0.12 s"),
              l(
                "手指离开键太急或太黏时。",
                "When fingers leave the keys too abruptly or too sluggishly.",
              ),
            ],
            [
              l("转向敏感度", "转向敏感度 / Steering sensitivity"),
              l("0.750", "0.750"),
              l(
                "身体晃得太勤，或一直不跟着音区转时。",
                "When the body sways too much, or never follows the register.",
              ),
            ],
            [
              l("速度限制剔除", "速度限制剔除 / Speed limit culling"),
              l("勾选 启用", "启用 / Enable ticked"),
              l(
                "想看看不做速度剔除是什么样子时（一般不用关）。",
                "When you want to see what happens without speed culling (usually leave it on).",
              ),
            ],
            [
              l("使用通道", "使用通道 / Use channel"),
              l("-1（所有通道）", "-1 (all channels)"),
              l(
                "一份 MIDI 混了多个通道、只想取其中一个时。",
                "When one MIDI mixes several channels and you want just one of them.",
              ),
            ],
            [
              l("FPS", "FPS"),
              l("60", "60"),
              l(
                "目标工程不是 60 帧时。",
                "When your target project does not run at 60 fps.",
              ),
            ],
            [
              l("音符量化", "音符量化 / Note quantization"),
              l("1/16", "1/16"),
              l(
                "打谱软件导出的整齐 MIDI，可以试试 无量化。",
                "For a neatly exported notation MIDI, try 无量化 / none.",
              ),
            ],
            [
              l(
                "帧数: / 手指筛选",
                "帧数: / Frame and 手指筛选 / Finger filters",
              ),
              l("留空 / 不启用", "empty / off"),
              l(
                "只有排查某几帧的问题时才用。",
                "Only when investigating specific frames.",
              ),
            ],
          ],
        },
      ],
    },

    // ═════════════════════════════════════════════════════════════
    // FAQ
    // ═════════════════════════════════════════════════════════════
    {
      id: "faq",
      title: l("参数相关的常见疑问", "Parameter FAQ"),
      blocks: [
        {
          kind: "p",
          text: l(
            "**改了动作参数要重新生成吗？** 要，但只重跑 🎹 2. 生成动画 / Generate Animation 就行——动作参数只在这一步用到，指法不用重算。",
            "**Do I have to regenerate after changing motion parameters?** Yes, but only 🎹 2. 生成动画 / Generate Animation — the motion parameters are used in that step alone, so the fingering does not need recomputing.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**改了 FPS 会有什么变化？** 总帧数按比例变：60 改成 30，帧数大约减半，动作颗粒变粗但工程更轻。播放速度和 FPS 是绑定的，所以调完要重新生成一遍。",
            "**What does changing FPS do?** The total frame count scales with it: going from 60 to 30 roughly halves the frames, giving coarser motion in a lighter project. Playback speed is tied to FPS, so regenerate after changing it.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**速度限制剔除 / Speed limit culling 到底影响了什么？** 它只在指法算完之后筛一遍，把「按手速上限算物理上够不到」的记录从输出里删掉。它不参与指法的取舍，所以不会改变左右手的分配。",
            "**What exactly does 速度限制剔除 / Speed limit culling affect?** It filters once, after fingering is computed, removing records that physically cannot be reached within the hand-speed ceiling. It never takes part in fingering decisions, so hand assignment is unchanged.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**单手跨度 / Hand span 为什么改不了？** 它是软件按角色配置里的键盘尺寸和手掌大小算出来的，不是手填参数。要让它变，得改角色数据本身，见《自定义人物进阶教程》。",
            "**Why can't I edit 单手跨度 / Hand span?** It is computed from the keyboard size and palm size in the performer's profile rather than typed in. Changing it means changing the performer data itself — see the Custom Performer tutorial.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**换过演奏者，动作却还是老样子？** 系统的初始化（钢琴键域、左右手手位）只做一次，之后不会自动重来。换角色后重启软件再生成；控制台里的「系统已初始化，如需重新初始化请先重置」就是在说这件事。",
            "**I switched performers but the motion is unchanged.** System initialization — piano key range and both hands' positions — happens once and does not repeat by itself. Restart the app after switching, then generate; the console warning that the system is already initialized is talking about exactly this.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**📋 记录查看 / Records 一直显示 未加载？** 先回 🎵 MIDI处理 确认选好了 MIDI 和轨道，并至少生成过一次指法；再回这一页点 🔄 刷新。这一页读的是磁盘上已经存在的 .hand 文件，不会自己去算。",
            "**📋 记录查看 / Records keeps showing 未加载.** Go back to 🎵 MIDI处理, make sure the MIDI and track are selected and that fingering has been generated at least once, then return here and click 🔄 刷新. This page only reads the .hand file that already exists on disk — it never computes anything.",
          ),
        },
        {
          kind: "p",
          text: l(
            "**控制台太长看不过来？** 点 复制全部 / Copy All，粘到文本编辑器里搜关键字（“完成”“错误”“剔除”）比在界面里翻快得多。",
            "**The console is too long to read.** Click 复制全部 / Copy All and search the pasted text for keywords — much faster than scrolling the panel.",
          ),
        },
      ],
    },
  ],
};
